import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Play, Rocket, FileSpreadsheet, Briefcase, ChevronRight,
  Search, PlusCircle, Loader2, AlertCircle, Check
} from 'lucide-react';
import { toast } from 'sonner';
import SectionGuide from '../../components/SectionGuide';
import { BookOpen } from 'lucide-react';
import { ProgressIndicator } from './ProgressIndicator';
import { ClientHistoryPanel } from './ClientHistoryPanel';

export function WizardStep1({
  step, animKey, stepAnimClass, guideResetKey,
  showProjectForm, setShowProjectForm,
  orgName, setOrgName, implName, setImplName,
  implDescription, setImplDescription,
  targetAudience, setTargetAudience,
  orgMode, setOrgMode, selectedOrgId, setSelectedOrgId,
  organizations, canCreateOrg,
  newOrgName, handleNewOrgNameChange,
  fuzzyLoading, fuzzyMatches, handleFuzzySelect,
  newOrgIndustry, setNewOrgIndustry,
  newOrgCountry, setNewOrgCountry,
  newOrgContactEmail, setNewOrgContactEmail,
  orgLookups, handleInlineOrgCreate, creatingOrg,
  pendingTemplate, setPendingTemplate,
  templates, applyTemplate, goToStep,
  scopeTemplates = [], applyScopeTemplate,
  setComponentDeliverables,
  setSelectedTemplate, setImplementationType, setComplexity,
  setModifiers, setSelectedComponents,
  setStep, setStepDirection, setAnimKey,
  launchQuickStart, setFuzzyMatches, setNewOrgName
}) {
  return (
    <div key={`step1-${animKey}`} className={stepAnimClass} data-testid="wizard-step-1">
      <SectionGuide id="estimator-overview" title="How Blueprint Studio Works" accent="#0F2040" icon={BookOpen} resetKey={guideResetKey}>
        <p>
          The <strong>Seertech Blueprint Studio</strong> calculates the effort, cost, and resource requirements for deploying a Learning Management System. Walk through a 4-step wizard: set up your project, pick an implementation type, choose a complexity tier, then fine-tune components and project factors to produce a detailed cost breakdown.
        </p>
        <p>
          <strong>Why use it:</strong> Instead of back-of-napkin guesses, you get a data-driven estimate grounded in Seertech's delivery experience — broken down by role, component group, and confidence level. Save, export (PDF/Word/Excel/PPT), generate an AI narrative, and feed the estimate into the <em>Project Planner</em> for a full timeline.
        </p>
      </SectionGuide>
      <ProgressIndicator step={step} goToStep={goToStep} />
      
      <div className="text-center mb-14">
        <h2 className="text-3xl md:text-4xl font-bold text-[#0F2040] font-['Outfit'] tracking-tight mb-4">
          How would you like to start?
        </h2>
        <p className="text-lg text-slate-500 max-w-xl mx-auto">
          Create a custom estimate or use a pre-built template
        </p>
      </div>

      <div className={`grid gap-8 max-w-5xl mx-auto mb-12 ${showProjectForm ? '' : 'md:grid-cols-3'}`}>
        {/* Quick Start Demo */}
        {!showProjectForm && (
        <Card 
          className="cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1 border border-amber-200 hover:border-amber-400 bg-gradient-to-b from-amber-50/50 to-white"
          onClick={launchQuickStart}
          data-testid="quick-start-demo"
        >
          <CardContent className="p-10 text-center">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mx-auto mb-6">
              <Rocket className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-[#0F2040] mb-3">Quick Start Demo</h3>
            <p className="text-slate-500 mb-5">See a full estimate in under 60 seconds</p>
            <Badge className="bg-amber-100 text-amber-700 font-medium">Instant</Badge>
          </CardContent>
        </Card>
        )}

        {/* Start Fresh / Project Details */}
        {!showProjectForm ? (
        <Card 
          className="cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1 border border-slate-200 hover:border-[#4ABFCF]/50"
          onClick={() => setShowProjectForm(true)}
          data-testid="start-fresh"
        >
          <CardContent className="p-10 text-center">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#0F2040] to-[#1a3a6e] flex items-center justify-center mx-auto mb-6">
              <Play className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-[#0F2040] mb-3">Start Fresh</h3>
            <p className="text-slate-500 mb-5">Build a custom estimate step-by-step</p>
            <Badge className="bg-slate-100 text-slate-500 font-medium">~5 minutes</Badge>
          </CardContent>
        </Card>
        ) : (
        <Card className="border-2 border-[#4ABFCF]/50 shadow-lg md:col-span-2" data-testid="project-details-form">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0F2040] to-[#1a3a6e] flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#0F2040]">Project Details</h3>
                <p className="text-sm text-slate-500">Tell us about the implementation</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {/* Organization Selection / Creation */}
              <div className="md:col-span-2 space-y-3">
                <label className="text-sm font-medium text-slate-700 block">Customer Organization <span className="text-red-400">*</span></label>

                {/* Toggle: Select Existing / Create New */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setOrgMode('select'); setFuzzyMatches([]); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${orgMode === 'select' ? 'bg-[#0F2040] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                    data-testid="org-mode-select"
                  >
                    <Search className="w-3 h-3" /> Select Existing
                  </button>
                  {canCreateOrg && (
                    <button
                      type="button"
                      onClick={() => { setOrgMode('create'); setSelectedOrgId(''); }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${orgMode === 'create' ? 'bg-[#0F2040] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                      data-testid="org-mode-create"
                    >
                      <PlusCircle className="w-3 h-3" /> Create New
                    </button>
                  )}
                </div>

                {/* Select Existing Org */}
                {orgMode === 'select' && (
                  <div>
                    <select
                      value={selectedOrgId}
                      onChange={(e) => {
                        const orgId = e.target.value;
                        setSelectedOrgId(orgId);
                        if (orgId) {
                          const org = organizations.find(o => o.org_id === orgId);
                          if (org) setOrgName(org.name);
                        } else {
                          setOrgName('');
                        }
                      }}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      data-testid="org-select"
                    >
                      <option value="">— Select an organization —</option>
                      {organizations.map(org => (
                        <option key={org.org_id} value={org.org_id}>{org.name}{org.industry ? ` (${org.industry})` : ''}</option>
                      ))}
                    </select>
                    {!selectedOrgId && organizations.length === 0 && (
                      <p className="text-xs text-slate-400 mt-1">No organizations available.{canCreateOrg && ' Switch to "Create New" to add one.'}</p>
                    )}
                  </div>
                )}

                {/* Create New Org */}
                {orgMode === 'create' && canCreateOrg && (
                  <div className="space-y-3 p-4 rounded-lg border border-dashed border-[#4ABFCF]/40 bg-[#4ABFCF]/5">
                    <div>
                      <Label className="text-xs">Organization Name <span className="text-red-400">*</span></Label>
                      <Input
                        value={newOrgName}
                        onChange={e => handleNewOrgNameChange(e.target.value)}
                        placeholder="e.g. Acme Corporation"
                        className="mt-1 text-sm"
                        data-testid="new-org-name-input"
                      />
                    </div>

                    {/* Fuzzy match suggestions */}
                    {fuzzyLoading && (
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        <Loader2 className="w-3 h-3 animate-spin" /> Checking for existing organizations...
                      </div>
                    )}
                    {!fuzzyLoading && fuzzyMatches.length > 0 && (
                      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="fuzzy-match-suggestions">
                        <div className="flex items-center gap-1.5 text-xs font-medium text-amber-700">
                          <AlertCircle className="w-3.5 h-3.5" />
                          Similar organizations already exist. Did you mean one of these?
                        </div>
                        {fuzzyMatches.map(match => (
                          <button
                            key={match.org_id}
                            type="button"
                            onClick={() => handleFuzzySelect(match)}
                            className="w-full flex items-center justify-between px-3 py-2 rounded-md bg-white border border-amber-100 hover:border-[#4ABFCF] hover:bg-[#4ABFCF]/5 transition-colors text-left"
                            data-testid={`fuzzy-match-${match.org_id}`}
                          >
                            <div>
                              <span className="text-sm font-medium text-[#0F2040]">{match.name}</span>
                              <span className="text-xs text-slate-400 ml-2">
                                {[match.industry, match.country].filter(Boolean).join(' \u00b7 ')}
                              </span>
                            </div>
                            <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">
                              {Math.round(match.score * 100)}% match
                            </Badge>
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Industry</Label>
                        <select
                          value={newOrgIndustry}
                          onChange={e => setNewOrgIndustry(e.target.value)}
                          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-xs mt-1"
                          data-testid="new-org-industry"
                        >
                          <option value="">— Select —</option>
                          {orgLookups.industries?.map(ind => <option key={ind} value={ind}>{ind}</option>)}
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">Country</Label>
                        <select
                          value={newOrgCountry}
                          onChange={e => setNewOrgCountry(e.target.value)}
                          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-xs mt-1"
                          data-testid="new-org-country"
                        >
                          <option value="">— Select —</option>
                          {orgLookups.countries?.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Contact Email <span className="text-slate-400 font-normal">(optional)</span></Label>
                      <Input
                        type="email"
                        value={newOrgContactEmail}
                        onChange={e => setNewOrgContactEmail(e.target.value)}
                        placeholder="client@acme.com"
                        className="mt-1 text-xs"
                        data-testid="new-org-contact-email"
                      />
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleInlineOrgCreate}
                      disabled={!newOrgName.trim() || creatingOrg}
                      className="bg-[#4ABFCF] hover:bg-[#3AAAB9]"
                      data-testid="create-org-inline-btn"
                    >
                      {creatingOrg ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <PlusCircle className="w-3.5 h-3.5 mr-1" />}
                      {creatingOrg ? 'Creating...' : 'Create Organization'}
                    </Button>
                  </div>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Organization Name <span className="text-red-400">*</span>
                  {selectedOrgId && <span className="text-[10px] text-[#4ABFCF] ml-1">(from selected org)</span>}
                </label>
                <Input
                  placeholder="e.g. Acme Corporation"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  readOnly={!!selectedOrgId}
                  className={selectedOrgId ? 'bg-slate-50 text-slate-600' : ''}
                  data-testid="org-name-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Implementation Name <span className="text-red-400">*</span></label>
                <Input
                  placeholder="e.g. Phase 1 LMS Rollout"
                  value={implName}
                  onChange={(e) => setImplName(e.target.value)}
                  data-testid="impl-name-input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Brief Description <span className="text-slate-400 font-normal">(optional)</span></label>
                <Input
                  placeholder="e.g. Enterprise-wide LMS deployment for onboarding and compliance training"
                  value={implDescription}
                  onChange={(e) => setImplDescription(e.target.value)}
                  data-testid="impl-description-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Target Audience Size <span className="text-slate-400 font-normal">(optional)</span></label>
                <Input
                  type="number"
                  placeholder="e.g. 5000"
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value)}
                  data-testid="target-audience-input"
                />
              </div>
            </div>
            <ClientHistoryPanel orgName={orgName} />
            <div className="flex items-center gap-3">
              <Button 
                onClick={() => { setShowProjectForm(false); setPendingTemplate(null); setOrgName(''); setImplName(''); setImplDescription(''); setTargetAudience(''); setSelectedOrgId(''); setOrgMode('select'); setNewOrgName(''); setFuzzyMatches([]); }}
                variant="outline"
                data-testid="project-form-cancel"
              >
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  if (pendingTemplate) {
                    // Scope-template branch (object payload from /api/scope-templates)
                    if (typeof pendingTemplate === 'object' && pendingTemplate.__scopeTemplate && applyScopeTemplate) {
                      const tpl = pendingTemplate.__scopeTemplate;
                      setSelectedTemplate(`scope:${tpl.id}`);
                      if (tpl.implementation_type) setImplementationType(tpl.implementation_type);
                      if (tpl.complexity) setComplexity(tpl.complexity);
                      if (tpl.modifiers) setModifiers(prev => ({ ...prev, ...tpl.modifiers }));
                      const newSelected = {};
                      const newDeliverables = {};
                      (tpl.components || []).forEach(c => {
                        newSelected[c.component_name] = c.quantity || 1;
                        const bullets = (c.deliverables || [])
                          .map(b => ({ id: b.id, text: b.text, is_milestone: !!b.is_milestone }))
                          .filter(b => (b.text || '').trim());
                        if (bullets.length > 0) newDeliverables[c.component_name] = bullets;
                      });
                      setSelectedComponents(newSelected);
                      if (setComponentDeliverables) setComponentDeliverables(newDeliverables);
                      setPendingTemplate(null);
                      setStepDirection('forward');
                      setAnimKey(prev => prev + 1);
                      setStep(4);
                      toast.success(`Applied template "${tpl.name}"`);
                      return;
                    }
                    // Static-template branch (string key)
                    const template = templates[pendingTemplate];
                    if (template) {
                      setSelectedTemplate(pendingTemplate);
                      setImplementationType(template.implementation_type);
                      setComplexity('standard');
                      setModifiers(template.default_modifiers);
                      const newSelected = {};
                      template.default_components.forEach(comp => {
                        newSelected[comp.component_name] = comp.quantity;
                      });
                      setSelectedComponents(newSelected);
                      setPendingTemplate(null);
                      setStepDirection('forward');
                      setAnimKey(prev => prev + 1);
                      setStep(4);
                    }
                  } else {
                    goToStep(2);
                  }
                }}
                disabled={!orgName.trim() || !implName.trim()}
                className="bg-[#0F2040] hover:bg-[#172E5A]"
                data-testid="project-form-continue"
              >
                Continue
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Use Template */}
        <Card className="border border-slate-200 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
          <CardContent className="p-10 text-center">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#4ABFCF] to-[#6DD3DF] flex items-center justify-center mx-auto mb-6">
              <FileSpreadsheet className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-[#0F2040] mb-3">Use a Template</h3>
            <p className="text-slate-500 mb-6">Start with a pre-configured scenario</p>
            
            <div className="space-y-3">
              {Object.entries(templates).map(([key, template]) => (
                <button
                  key={key}
                  onClick={() => applyTemplate(key)}
                  className="w-full p-3.5 rounded-xl border border-slate-200 hover:border-[#4ABFCF]/50 hover:bg-[#4ABFCF]/5 transition-all text-left flex items-center justify-between group"
                  data-testid={`template-${key}`}
                >
                  <span className="font-medium text-[#0F2040]">{template.name}</span>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-[#4ABFCF] transition-colors" />
                </button>
              ))}
            </div>

            {scopeTemplates.length > 0 && applyScopeTemplate && (
              <div className="mt-5 pt-4 border-t border-slate-100" data-testid="scope-templates-section">
                <div className="text-[11px] font-semibold tracking-wide uppercase text-slate-400 mb-2 text-left">
                  Saved Scope Templates
                </div>
                <div className="space-y-2">
                  {scopeTemplates.map((tpl) => (
                    <button
                      key={tpl.id}
                      onClick={() => applyScopeTemplate(tpl)}
                      className="w-full p-3 rounded-xl border border-amber-200 hover:border-amber-400 hover:bg-amber-50/50 transition-all text-left flex items-start justify-between group"
                      data-testid={`scope-template-${tpl.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[#0F2040] text-sm truncate">{tpl.name}</div>
                        {tpl.description && (
                          <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2 text-left">{tpl.description}</div>
                        )}
                        <div className="text-[10px] text-slate-400 mt-1">
                          {tpl.component_count} component{tpl.component_count === 1 ? '' : 's'}
                          {tpl.milestone_count > 0 && <> · {tpl.milestone_count} milestone{tpl.milestone_count === 1 ? '' : 's'}</>}
                          {tpl.implementation_type && <> · {tpl.implementation_type}</>}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-amber-500 transition-colors mt-0.5 shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
